/* eslint-disable @typescript-eslint/no-empty-function */
import React, { lazy, Suspense } from 'react';
import { reaction } from 'mobx';
import { disposeOnUnmount } from 'mobx-react';
import connect from '@src/js/separate-logic/connect';
import ImageElementPresenter from './image-element-presenter';
import omit from 'lodash/omit';
import GraphicContent from '../../../graphic-content';
import * as commonFun from '../../../common';
import { omitStyle } from '@src/slide/infrastructure/module/independent/assert/get-set';
import ShapeLinkFlag from '../../../shape-link-flag';
import { getSizeAndFlip } from './common';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import ImageModel from './image-element-model';
import { CommonContainer } from '@src/js/service/component-service/commonContainer';
import { AttachmentClassify, AUDIO_DEFAULT_IMG } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import '../index.css';
import ErrorBoundary from '@com/error-boundary';
import { isDomGifPictureModel, isVideoElement, isAttachmentImageElement, isAudioElement } from '@src/slide/domain/ui-model/elements/element-judge';
import Attachment from '../../../attachment';
import { IMediaManager, MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { RenderPostionType } from '@src/js/constant';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';

const MediaContainer = lazy(() => import(/* webpackChunkName: "media-container" */ '../media-container'));

interface ImageElementProps {
    // 父组件传递的props
    component: any;
    rect: any;
    isCroping: boolean;
    rotate: number;
    fromScale: number;
    relativeRotate: number;
    relativeFlipH: number;
    relativeFlipV: number;
    editable: boolean;
    isPreview: boolean;
    region: string;
    getPageScale: () => number;
    onElementUpdate: () => void;

    // dispatchFun
    handleDblClick: any;
    handleMouseEnter: (e) => void;
    handleMouseLeave: (e) => void;
    handleClick: (e) => void;
    handleTouchStart: (e) => void;

    setRef: (name: string, ref: any) => void;
    // model
    isEditMode: boolean;
    serverRender: boolean;
    shouldRenderMedia: boolean;
    getMediaManager(): IMediaManager | undefined;
}

export class ImageElement extends React.Component<ImageElementProps> {
    public static displayName = 'ImageElement'; // 声明displayName，单元测试用于查找组件

    public static defaultProps = {
        setRef: () => {},
        handleClick: () => {},
        handleDblClick: () => {},
        handleMouseEnter: () => {},
        handleMouseLeave: () => {},
        handleTouchStart: () => {},
    };

    public componentDidMount(): void {
        const mediaManager = this.props.component?.getMediaManager();
        const componentKey = this.props.component?.key;
        const componentId = this.props.component?.id;
        const attachmentManager = this.props.component?.getAttachmentManager();
        if (!mediaManager || !componentKey || !attachmentManager || !componentId) {
            return;
        }
        disposeOnUnmount(this, reaction(
            () => mediaManager.shouldRenderDomGif(componentKey),
            () => {
                this.forceUpdate();
            },
        ));

        disposeOnUnmount(this, reaction(
            () => mediaManager.shouldShowMediaElement(componentKey, componentId, this.getMediaRegion()),
            () => {
                this.forceUpdate();
            },
        ));

        disposeOnUnmount(this, reaction(
            () => attachmentManager.attachmentErrorHappen,
            () => {
                this.forceUpdate();
            },
        ));
    }

    /* eslint-disable complexity */
    public render() {
        const {
            component,
            rect,
            isCroping,
            handleDblClick,
            handleMouseEnter,
            handleMouseLeave,
            handleClick,
            getPageScale,
            rotate,
            fromScale,
            relativeRotate,
            relativeFlipH,
            relativeFlipV,
            editable,
            onElementUpdate,
            handleTouchStart,
            setRef,
        } = this.props;

        const componentProps = component.props;
        const width = rect ? rect.width : componentProps.style.width;
        const height = rect ? rect.height : componentProps.style.height;
        const srcRect = rect?.srcRect
            ? rect.srcRect
            : componentProps.style.srcRect;
        const { src, backupSrc, imageType } = component.props;
        const mediaManager = this.props.getMediaManager();

        let pl = 0; let pr = 0; let pt = 0; let pb = 0; let rw = 0; let rh = 0;

        pl = (width * srcRect.l) / (1 - srcRect.l - srcRect.r);
        pr = (width * srcRect.r) / (1 - srcRect.l - srcRect.r);

        pt = (height * srcRect.t) / (1 - srcRect.t - srcRect.b);
        pb = (height * srcRect.b) / (1 - srcRect.t - srcRect.b);

        rw = width + pl + pr;
        rh = height + pt + pb;

        if (isNaN(rw))  rw = 0;
        if (isNaN(rh))  rh = 0;

        // @ts-ignore
        pl = `${-pl}px`;
        // @ts-ignore
        pr = `${-pr}px`;
        // @ts-ignore
        pt = `${-pt}px`;
        // @ts-ignore
        pb = `${-pb}px`;

        const { size, flip } = getSizeAndFlip(rect, componentProps);
        const bgShapeOptions = { width, height, bgShape: componentProps.style.bgShape };

        const controlValues = component.operatingGraphicInfo
            ? component.operatingGraphicInfo.controlValues
            : componentProps.style.controlValues;

        const isContainerDisplay = ((
            (isVideoElement(component) || isAudioElement(component))
             && mediaManager?.shouldShowMediaElement(component.key, component.id as string, this.getMediaRegion())
        )
            || (isDomGifPictureModel(component) && mediaManager?.shouldRenderDomGif(component.key))
            || this.isInMobileSlideList()
            || isAttachmentImageElement(component)) && ((component as IElementBaseModel).hidden !== 1);

        const showMediaEle = this.isNeedShowMediaEle();
        const showImageAttachment = component.props?.attachment?.classify === AttachmentClassify.IMAGE;
        const showNormalImage = !showMediaEle && !showImageAttachment;
        return (
            <CommonContainer
                type={this.containerType()}
                style={{
                    display: isContainerDisplay
                        ? 'block'
                        : 'none',
                    pointerEvents: (
                        isAttachmentImageElement(component)
                        && component.selected
                        && !this.getEditingStateManager().isDragging
                        // 移动端列表页需要响应点击事件播放音视频
                    ) || this.isInMobileSlideList()
                        ? 'auto'
                        : 'none',
                }}
            >
                <CommonContainer
                    type={this.containerType()}
                    style={{
                        transformOrigin: null,
                        transform: commonFun.transformOfFlip(flip),
                        width: size.width,
                        height: size.height,
                        // position: 'absolute', 去除无用position，减少回流时间
                        display: 'flex', // 修复部分图形去掉SVG position后，svg有偏移问题
                        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                    }}
                    onDoubleClick={isCroping ? null : handleDblClick}
                    ref={ref => setRef('elementContainerRef', ref)}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleTouchStart}
                    onClick={handleClick}
                >
                    {
                        showNormalImage && (
                            <>
                                <GraphicContent
                                    ref={ref => setRef('backgroundRef', ref)}
                                    model={{
                                        ...omit(omitStyle(component.props.style), 'width', 'height'),
                                        ...size,
                                        rotate,
                                        controlValues,
                                    }}
                                    scale={getPageScale()}
                                    editable={this.getEditable()}
                                    relativeRotate={relativeRotate}
                                    relativeFlipH={relativeFlipH}
                                    relativeFlipV={relativeFlipV}
                                    selected={component.selected}
                                    imageBorderOutside={true}
                                    onImgLoaded={onElementUpdate}
                                    mkey={component.key}
                                />
                                {
                                    this.renderForeground(
                                        size, this.generateImageSource(),
                                        rw, rh, pl, pt, backupSrc, imageType,
                                    )
                                }
                            </>
                        )
                    }
                </CommonContainer>
                {
                    // 链接标识小图标
                    fromScale === 0 ? (
                        <ShapeLinkFlag
                            editable={editable}
                            data={component.props.link}
                            mkey={component.key}
                            selected={component.selected}
                        />
                    ) : null
                }
                {showMediaEle && this.renderMediaEle(size, isContainerDisplay, srcRect, bgShapeOptions)}
                {showImageAttachment && this.renderAttachmentImage()}
            </CommonContainer>
        );
    }

    private getMediaRegion = () => (this.isInMobileSlideList() ? MediaRegion.MOBILE_PREVIEW : MediaRegion.EDITOR);

    private containerType = () => ('div');

    private renderAttachmentImage = () => {
        const { attachment } = this.props.component.props;

        return (
            <Attachment
                attachment={attachment}
                uiModel={this.props.component}
            />
        );
    };

    private renderMediaEle = (
        elementSize: Record<string, number>,
        isContainerDisplay: boolean,
        srcRect: Record<string, number>,
        bgShapeOptions: any,
    ) => {
        const {
            component,
            getPageScale,
        } = this.props;

        return (
            <ErrorBoundary>
                <Suspense fallback={<div></div>}>
                    <MediaContainer
                        video={component.video}
                        videoPoster={component.videoPoster}
                        audio={component.audio}
                        selected={component.selected}
                        imgSrc={component.props.src}
                        isInMobileSlideList={this.isInMobileSlideList()}
                        uiModel={component}
                        pageScale={getPageScale()}
                        elementSize={elementSize}
                        mediaManager={this.props.getMediaManager()}
                        region={this.getMediaRegion()}
                        isContainerDisplay={isContainerDisplay}
                        srcRect={srcRect}
                        bgShapeOptions={bgShapeOptions}
                    />
                </Suspense>
            </ErrorBoundary>
        );
    };

    private isNeedShowMediaEle = () => {
        const {
            component,
            serverRender,
        } = this.props;

        return !serverRender && !!(component.video || component.audio);
    };

    private isInMobileSlideList = () => this.props.region === RenderPostionType.MOBILEBROWSE;

    private generateImageSource = () => {
        const { component } = this.props;

        if (isVideoElement(component)) {
            return component.videoPoster;
        }

        return component.props.src;
    };


    // 前景
    private renderForeground = (size, src, rw, rh, pl, pt, backupSrc, imageType) => {
        const {
            component,
            rotate,
            setRef,
            onElementUpdate,
            isCroping,
            relativeRotate,
            relativeFlipH,
            relativeFlipV,
            isPreview,
        } = this.props;

        const foregroundModel = {
            ...omit(omitStyle(component.props.style), 'width', 'height', 'border'),
            ...size,
            rotate,
        };
        const image = {
            src: component.audio ? AUDIO_DEFAULT_IMG : src,
            backupSrc,
            imageType,
            width: rw,
            height: rh,
            pl,
            pt,
            alpha: component.props.style.alpha,
            isForeground: true,
        };
        const fillBackground = {
            image,
        };
        foregroundModel.fillBackground = fillBackground;
        if (component.props.syncLoad) {
            // @ts-ignore
            image.syncLoad = true;
        }

        const componentProps = component.props;
        const controlValues = component.operatingGraphicInfo
            ? component.operatingGraphicInfo.controlValues
            : componentProps.style.controlValues;
        foregroundModel.controlValues = controlValues;

        return (
            <GraphicContent
                ref={ref => setRef('foregroundRef', ref)}
                className={isCroping ? 'areaUnDisplay' : ''}
                model={foregroundModel}
                editable={this.getEditable()}
                relativeRotate={relativeRotate}
                relativeFlipH={relativeFlipH}
                relativeFlipV={relativeFlipV}
                pointerEvents={isCroping || !component.selectable ? 'none' : null}
                onImgLoaded={onElementUpdate}
                isPreview={isPreview}
                style={{ position: 'absolute' }}
                mkey={`${component.key}foreground`}
            />
        );
    };

    private getEditable() {
        const { isCroping, editable } = this.props;
        return (browser.isMobile || isCroping) ? false : editable;
    }

    private getEditingStateManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .viewableServiceLayer.editingStateManager;
    }
}

export default connect(
    ImageElementPresenter,
    ImageModel,
    ImageElement,
);
